// First-party signup attribution. Shared cookie contract with the marketing
// site (marketing-website-tento-com BaseLayout.astro) and volt-portal
// (src/lib/attribution.ts): `tento_attr` holds a JSON payload captured at the
// visitor's FIRST landing on any Tento property (first-touch — never
// overwritten once set), 90-day TTL, scoped to the root domain so it
// survives cross-subdomain navigation and the Auth0 redirect. Signup flows
// send it with the account-creation request; node-backend classifies and
// stamps it on the Account (signupAttribution* columns).
//
// v2 adds `vid` — a UUID minted at first touch that is the visitor's stable
// cross-app identity (Meta `external_id`, tracking_event.anonymous_id) —
// and the `_ta` hand-off: cookies don't cross tento.com <-> tento.co, so the
// marketing site forwards its payload on every app-bound link as the `_ta`
// query param (base64url JSON). We adopt it verbatim when this domain has
// no cookie yet, and strip it from the URL either way.

const COOKIE_NAME = 'tento_attr';
const FORWARD_PARAM = '_ta';
const MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
const PAYLOAD_VERSION = 2;
const CAMPAIGN_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
];
const TENTO_HOST_RE = /(^|\.)tento\.(com|co)$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const cookieValue = (name) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return match ? match.slice(name.length + 1) : null;
};

const newVisitorId = () => {
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && c.randomUUID) return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && c.getRandomValues) c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const str = (v, max) => (typeof v === 'string' && v !== '' ? v.slice(0, max) : undefined);

/** Strict allow-list decode of a forwarded `_ta` payload (anyone can craft a link). */
const decodeForwarded = (raw) => {
  if (!raw) return null;
  try {
    const b64 = raw.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      Array.from(atob(b64), (ch) => `%${ch.charCodeAt(0).toString(16).padStart(2, '0')}`).join('')
    );
    const p = JSON.parse(json);
    if (!p || typeof p !== 'object' || Array.isArray(p)) return null;
    const lh = (str(p.lh, 200) || '').toLowerCase();
    if (!lh || !TENTO_HOST_RE.test(lh)) return null;
    const d = { v: PAYLOAD_VERSION, lh };
    const vid = str(p.vid, 36);
    if (vid && UUID_RE.test(vid)) d.vid = vid.toLowerCase();
    d.ts = typeof p.ts === 'number' && Number.isFinite(p.ts) && p.ts > 0 ? Math.floor(p.ts) : Date.now();
    const lp = str(p.lp, 700);
    if (lp && lp.startsWith('/')) d.lp = lp;
    const r = str(p.r, 500);
    if (r) d.r = r;
    const ir = (str(p.ir, 200) || '').toLowerCase();
    if (ir && TENTO_HOST_RE.test(ir)) d.ir = ir;
    CAMPAIGN_KEYS.forEach((k) => {
      const v = str(p[k], 200);
      if (v) d[k] = v;
    });
    return d;
  } catch (e) {
    return null;
  }
};

const stripForwardParam = () => {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(FORWARD_PARAM)) return;
    url.searchParams.delete(FORWARD_PARAM);
    window.history.replaceState(window.history.state, '', url.toString());
  } catch (e) {
    // cosmetic only
  }
};

const captureLocal = () => {
  const d = {
    v: PAYLOAD_VERSION,
    vid: newVisitorId(),
    ts: Date.now(),
    lh: window.location.hostname,
    lp: window.location.pathname + window.location.search,
  };
  if (document.referrer) {
    try {
      const rh = new URL(document.referrer).hostname;
      if (TENTO_HOST_RE.test(rh)) d.ir = rh;
      else d.r = document.referrer.slice(0, 500);
    } catch (e) {
      // unparsable referrer — drop it
    }
  }
  const q = new URLSearchParams(window.location.search);
  CAMPAIGN_KEYS.forEach((k) => {
    const v = q.get(k);
    if (v) d[k] = v.slice(0, 200);
  });
  return d;
};

const cookieDomain = () => {
  const host = window.location.hostname;
  return /(^|\.)tento\.com$/.test(host)
    ? ';domain=.tento.com'
    : /(^|\.)tento\.co$/.test(host)
    ? ';domain=.tento.co'
    : '';
};

const writeCookie = (d) => {
  // Browsers silently discard cookie writes past ~4KB — and a discarded
  // write means a fresh vid on EVERY page load. Trim the bulky free-text
  // fields until the payload fits; vid and campaign params always survive.
  const payload = { ...d };
  let encoded = encodeURIComponent(JSON.stringify(payload));
  ['r', 'lp'].forEach((drop) => {
    if (encoded.length <= 3800) return;
    delete payload[drop];
    encoded = encodeURIComponent(JSON.stringify(payload));
  });
  document.cookie = `${COOKIE_NAME}=${encoded}${cookieDomain()};path=/;max-age=${MAX_AGE_SECONDS};SameSite=Lax;Secure`;
};

const hasCampaignParams = (d) => CAMPAIGN_KEYS.some((k) => typeof d[k] === 'string' && d[k] !== '');

/**
 * In-place upgrade of an EXISTING cookie — first-touch data is never
 * overwritten, but blanks are filled: a missing `vid` (pre-v2 cookies) is
 * taken from a forwarded `_ta` payload or minted, and campaign params are
 * recorded when the cookie has none at all. Mirrors volt-portal
 * src/lib/attribution.ts. Returns true when the cookie was rewritten.
 */
const upgradeExisting = (existing, forwarded) => {
  let changed = false;
  if (typeof existing.vid !== 'string' || !UUID_RE.test(existing.vid)) {
    existing.vid = forwarded && typeof forwarded.vid === 'string' ? forwarded.vid : newVisitorId();
    changed = true;
  }
  if (!hasCampaignParams(existing)) {
    const q = new URLSearchParams(window.location.search);
    CAMPAIGN_KEYS.forEach((k) => {
      const fromUrl = q.get(k);
      const v =
        (fromUrl && fromUrl.slice(0, 200)) ||
        (forwarded && typeof forwarded[k] === 'string' ? forwarded[k] : undefined);
      if (v) {
        existing[k] = v;
        changed = true;
      }
    });
  }
  if (changed) existing.v = PAYLOAD_VERSION;
  return changed;
};

/**
 * First-touch capture. An existing cookie is kept (only upgraded in place —
 * see upgradeExisting); the `_ta` param is stripped from the URL either way.
 */
export const captureAttribution = () => {
  try {
    if (typeof window === 'undefined') return;
    const forwardedRaw = new URLSearchParams(window.location.search).get(FORWARD_PARAM);
    const forwarded = decodeForwarded(forwardedRaw);
    const existing = readAttribution();
    if (existing) {
      upgradeExisting(existing, forwarded);
      // Always rewrite: the max-age was absolute from first touch (a
      // daily-active visitor re-minted on day 91; Safari/ITP caps script
      // cookies at 7 days). Rolling TTL; first-touch DATA never changes.
      writeCookie(existing);
      if (forwardedRaw) stripForwardParam();
      return;
    }
    const d = forwarded || captureLocal();
    if (!d.vid) d.vid = newVisitorId();
    writeCookie(d);
    if (forwardedRaw) stripForwardParam();
  } catch (e) {
    // attribution must never break the page
  }
};

/** Parsed cookie payload, or null when absent/corrupt (backend stamps 'unknown'). */
export const readAttribution = () => {
  try {
    const raw = cookieValue(COOKIE_NAME);
    if (!raw) return null;
    const parsed = JSON.parse(decodeURIComponent(raw));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
};

/** Stable first-party visitor id (tento_attr.vid), when one has been captured. */
export const readVisitorId = () => {
  const attr = readAttribution();
  const vid = attr ? attr.vid : null;
  return typeof vid === 'string' && UUID_RE.test(vid) ? vid.toLowerCase() : null;
};

// ---- Session id -----------------------------------------------------------
//
// `tento_sid` — rolling 30-minute session cookie on the registrable domain
// (mirrors volt-portal src/lib/attribution.ts) so one session survives
// subdomain hops and the Auth0 redirect. Fills tracking_event.session_id,
// which was 100% NULL before 2026-09-09.

const SESSION_COOKIE = 'tento_sid';
const SESSION_MAX_AGE_SECONDS = 30 * 60;

/** Current session id, minting and/or extending it as a side effect. */
export const touchSessionId = () => {
  try {
    if (typeof document === 'undefined') return null;
    const existing = cookieValue(SESSION_COOKIE);
    const sid = existing && UUID_RE.test(existing) ? existing : newVisitorId();
    document.cookie = `${SESSION_COOKIE}=${sid}${cookieDomain()};path=/;max-age=${SESSION_MAX_AGE_SECONDS};SameSite=Lax;Secure`;
    return cookieValue(SESSION_COOKIE) === sid ? sid : null;
  } catch (e) {
    return null;
  }
};
